import { UseQueryDefaultOptions } from '@front/hook/Api/common';
import { importTableDefinitionQueryKeys, useImportTableDefinition } from '@front/hook/Api/ImportTableDefinition';
import { DEFAULT_TIMEOUT } from '@front/hook/Api/utils/consts';
import { signalToTimeout } from '@front/hook/Api/utils/signalToTimeout';
import { ImportTableDefinitionRepository } from '@front/repository/Api/ImportTableDefinition';
import { useQueries } from '@tanstack/react-query';
import { AuthDomain } from 'auth-lib-front';
import { useMemo } from 'react';
import { DomainV2 } from 'troppus-common/domain';

import Project = AuthDomain.Object.Project;
import ImportTableDefinition = DomainV2.Import.ImportTableDefinition;
import ImportTableDefinitionRevision = DomainV2.Import.ImportTableDefinitionRevision;

const repository = new ImportTableDefinitionRepository();

/**
 * `ImportTableDefinitionRevision.Id` から所属する `ImportTableDefinition.Detail` を引ける Map を返す。
 * プロジェクト内の全テーブル定義のリビジョンを並列取得して構築するため、
 * テーブル定義数が増えるとリクエスト数も増える点に注意。
 */
export type UseTableDefinitionByRevisionIdResult = {
  map: Map<ImportTableDefinitionRevision.Id, ImportTableDefinition.Detail>;
  /** 定義一覧または各定義のリビジョン取得が完了していないとき true（マップへの解決待ち） */
  isLoading: boolean;
};

export function useTableDefinitionByRevisionId(projectId: Project.Id): UseTableDefinitionByRevisionIdResult {
  const { data: definitions = [], isPending: isDefinitionsPending } = useImportTableDefinition.List(projectId);

  const revisionQueries = useQueries({
    queries: definitions.map(({ definition }) => ({
      queryKey: importTableDefinitionQueryKeys.revisions(projectId, definition.id),
      queryFn: async ({ signal }: { signal: AbortSignal }) =>
        repository.listRevisions({ projectId, id: definition.id }, { signal: signalToTimeout(DEFAULT_TIMEOUT.QUERY, signal) }),
      ...UseQueryDefaultOptions,
    })),
  });

  const revisionsByDefinitionIndex = revisionQueries.map((q) => q.data);
  const isAnyRevisionPending = revisionQueries.some((q) => q.isPending);

  const map = useMemo(() => {
    const next = new Map<ImportTableDefinitionRevision.Id, ImportTableDefinition.Detail>();
    definitions.forEach((detail, index) => {
      const revisions = revisionsByDefinitionIndex[index] ?? [];
      for (const revision of revisions) {
        next.set(revision.id, detail);
      }
    });
    return next;
  }, [definitions, revisionsByDefinitionIndex]);

  return {
    map,
    isLoading: isDefinitionsPending || isAnyRevisionPending,
  };
}
